import React, { useState, useEffect, useRef, useCallback } from 'react'

// 1. IMPORTAMOS LAS IMÁGENES COMO MÓDULOS DE REACT
import imgElementNormal from '../../../../assets/games/luchoFest/element_normal.png';
import imgElementHit from '../../../../assets/games/luchoFest/element_hit.png';
import imgElementBonus1 from '../../../../assets/games/luchoFest/element_bonus.png';
import imgElementBonus2 from '../../../../assets/games/luchoFest/element_bonus_hit.png';
import imgElementWrong from '../../../../assets/games/luchoFest/element_wrong.png';
import imgBox from '../../../../assets/games/luchoFest/box.png';
import imgBgNormal from '../../../../assets/games/luchoFest/background_normal.jpg';
import imgBgBonus from '../../../../assets/games/luchoFest/background_bonus.jpg';
import imgBrand from '../../../../assets/games/luchoFest/brand.png';

// 2. ASIGNAMOS LAS VARIABLES IMPORTADAS A NUESTRO OBJETO
const GAME_ASSETS = {
  ELEMENT_NORMAL: imgElementNormal,
  ELEMENT_HIT: imgElementHit,
  ELEMENT_BONUS_1: imgElementBonus1,
  ELEMENT_BONUS_2: imgElementBonus2,
  ELEMENT_WRONG: imgElementWrong,
  BOX: imgBox,
  BACKGROUND_NORMAL: imgBgNormal,
  BACKGROUND_BONUS: imgBgBonus,
  BRAND: imgBrand
};

const LuchoFestGame = ({ onGameFinishEvent, gameSequence = [] }) => {
  const [currentGameState, setCurrentGameState] = useState('preloading'); 
  
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [activePhaseType, setActivePhaseType] = useState(null);

  const [currentScore, setCurrentScore] = useState(0);
  const [countdownSeconds, setCountdownSeconds] = useState(3);
  
  const [holesGridState, setHolesGridState] = useState(Array(6).fill({ isRaised: false, targetType: 'normal', isHit: false }));
  
  const [isHitFlashActive, setIsHitFlashActive] = useState(false);
  const [isMissFlashActive, setIsMissFlashActive] = useState(false);
  const [isBombFlashActive, setIsBombFlashActive] = useState(false);
  const [isScreenShaking, setIsScreenShaking] = useState(false);
  const [floatingTextsArray, setFloatingTextsArray] = useState([]);
  const [statusAlertObject, setStatusAlertObject] = useState(null);
  const [bonusTapLabelsArray, setBonusTapLabelsArray] = useState([]);
  
  const [currentBonusImageSrc, setCurrentBonusImageSrc] = useState(GAME_ASSETS.ELEMENT_BONUS_1);
  const [currentBackgroundImageSrc, setCurrentBackgroundImageSrc] = useState(GAME_ASSETS.BACKGROUND_NORMAL);

  // --- REFERENCIAS MAESTRAS PARA RENDIMIENTO ---
  const mutableScoreReference = useRef(0);
  const isPlayerBlindedReference = useRef(false);
  const phaseTimerReference = useRef(null);
  const targetAppearanceTimeoutReference = useRef(null);
  const isBombPendingReference = useRef(false);
  const hasGameFinishedReference = useRef(false);
  
  const activeHolesRef = useRef(Array(6).fill(false)); 
  const isPhaseRunningRef = useRef(false);
  
  // Referencias para controlar la velocidad dinámica
  const minimumAppearanceTimeReference = useRef(800);
  const maximumAppearanceTimeReference = useRef(1200);

  // --- PRECARGA DE IMÁGENES ---
  useEffect(() => {
    const imagesToPreloadList = Object.values(GAME_ASSETS);
    let successfullyLoadedImagesCount = 0;

    imagesToPreloadList.forEach((imageSource) => {
      const temporaryImageElement = new Image();
      temporaryImageElement.src = imageSource;
      temporaryImageElement.onload = () => {
        successfullyLoadedImagesCount++;
        if (successfullyLoadedImagesCount === imagesToPreloadList.length) setCurrentGameState('countdown');
      };
      temporaryImageElement.onerror = () => {
        successfullyLoadedImagesCount++;
        if (successfullyLoadedImagesCount === imagesToPreloadList.length) setCurrentGameState('countdown');
      };
    });
  }, []);

  // --- CUENTA REGRESIVA ---
  useEffect(() => {
    if (currentGameState === 'countdown') {
      if (countdownSeconds > 0) {
        const countdownTimerId = setTimeout(() => setCountdownSeconds(countdownSeconds - 1), 1000);
        return () => clearTimeout(countdownTimerId);
      } else {
        setCurrentGameState('running_phases');
      }
    }
  }, [currentGameState, countdownSeconds]);

  // --- ORQUESTADOR MAESTRO DE FASES ---
  useEffect(() => {
    if (currentGameState !== 'running_phases') return;

    if (currentPhaseIndex >= gameSequence.length) {
      finalizeGameEngine();
      return;
    }

    const currentPhaseConfig = gameSequence[currentPhaseIndex];
    setActivePhaseType(currentPhaseConfig.type);

    // Limpieza de seguridad
    isPhaseRunningRef.current = false;
    clearInterval(phaseTimerReference.current);
    clearTimeout(targetAppearanceTimeoutReference.current);

    if (currentPhaseConfig.type === 'normal') {
      // Pasamos la configuración de velocidad personalizada (si existe), o usamos un objeto vacío por defecto
      startNormalPhase(currentPhaseConfig.duration, currentPhaseConfig.bombs || 0, currentPhaseConfig.speedConfig || {});
    } else if (currentPhaseConfig.type === 'bonus') {
      startBonusPhase(currentPhaseConfig.duration);
    }

    return () => {
      isPhaseRunningRef.current = false;
      clearInterval(phaseTimerReference.current);
      clearTimeout(targetAppearanceTimeoutReference.current);
    };
  }, [currentGameState, currentPhaseIndex, gameSequence]);


  // --- EJECUTOR: FASE NORMAL ---
  // AHORA RECIBE LA CONFIGURACIÓN DE VELOCIDAD DIRECTO DEL PADRE
  const startNormalPhase = useCallback((phaseDuration, totalBombs, speedConfig) => {
    
    // 1. APLICAR CONFIGURACIÓN DE VELOCIDAD PARAMETRIZADA
    // Si el Admin manda un valor, lo usamos. Si no, usamos el valor por defecto de siempre.
    minimumAppearanceTimeReference.current = speedConfig.minTime || 800;
    maximumAppearanceTimeReference.current = speedConfig.maxTime || 1200;
    const currentPhaseAccelerationMultiplier = speedConfig.accelerationMultiplier || 0.95;

    isPhaseRunningRef.current = true;
    setCurrentBackgroundImageSrc(GAME_ASSETS.BACKGROUND_NORMAL);
    setHolesGridState(Array(6).fill({ isRaised: false, targetType: 'normal', isHit: false }));
    activeHolesRef.current = Array(6).fill(false); // Liberar todos los hoyos
    
    // 2. MENSAJE INTELIGENTE: Evalúa si es el inicio del juego o una fase intermedia
    const phaseWelcomeMessage = currentPhaseIndex === 0 ? "¡A JUGAR!" : "¡SIGUE EL JUEGO!";
    showTemporaryCenterAlert(phaseWelcomeMessage, "#10b981");

    let remainingTimeInSeconds = phaseDuration;
    
    // Configurar bombas aleatorias
    let availableSecondsArray = Array.from({ length: phaseDuration - 1 }, (_, index) => index + 1);
    for (let index = availableSecondsArray.length - 1; index > 0; index--) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [availableSecondsArray[index], availableSecondsArray[randomIndex]] = [availableSecondsArray[randomIndex], availableSecondsArray[index]];
    }
    const scheduledBombSecondsList = availableSecondsArray.slice(0, totalBombs);

    // Motor Seguro de Aparición
    const spawnRandomTarget = () => {
      if (!isPhaseRunningRef.current) return;

      const availableIndices = [];
      activeHolesRef.current.forEach((isOccupied, index) => {
        if (!isOccupied) availableIndices.push(index);
      });

      // Si todos los hoyos están llenos, intentar de nuevo en 200ms
      if (availableIndices.length === 0) {
        targetAppearanceTimeoutReference.current = setTimeout(spawnRandomTarget, 200);
        return;
      }

      const randomlySelectedHole = availableIndices[Math.floor(Math.random() * availableIndices.length)];
      
      const isCurrentTargetBomb = isBombPendingReference.current;
      if (isCurrentTargetBomb) isBombPendingReference.current = false;

      // Marcar hoyo como ocupado inmediatamente
      activeHolesRef.current[randomlySelectedHole] = true;

      setHolesGridState(previousHolesGrid => {
        const updatedHolesGrid = [...previousHolesGrid];
        updatedHolesGrid[randomlySelectedHole] = { isRaised: true, targetType: isCurrentTargetBomb ? 'bomb' : 'normal', isHit: false };
        return updatedHolesGrid;
      });
      
      // Calculamos cuánto tiempo estará arriba usando las referencias actuales (que se van reduciendo)
      const timeToStayUp = Math.random() * (maximumAppearanceTimeReference.current - minimumAppearanceTimeReference.current) + minimumAppearanceTimeReference.current;
      
      // Programar la bajada Y el siguiente globo
      targetAppearanceTimeoutReference.current = setTimeout(() => {
        if (!isPhaseRunningRef.current) return;

        activeHolesRef.current[randomlySelectedHole] = false; // Liberar hoyo
        
        setHolesGridState(currentGridState => {
          const safeUpdatedGrid = [...currentGridState];
          if (!safeUpdatedGrid[randomlySelectedHole].isHit) {
            safeUpdatedGrid[randomlySelectedHole].isRaised = false;
          }
          return safeUpdatedGrid;
        });

        // 3. ACELERACIÓN CORREGIDA:
        // En lugar de acelerar cada segundo del reloj (que era muy rápido o fallaba), 
        // aceleramos CADA VEZ que un globo desaparece. Es más fluido y proporcional a la acción.
        minimumAppearanceTimeReference.current *= currentPhaseAccelerationMultiplier;
        maximumAppearanceTimeReference.current *= currentPhaseAccelerationMultiplier;

        // Establecemos un "piso" de seguridad para que la velocidad nunca llegue a 0ms
        // (150ms es el límite de lo jugable por reflejos humanos)
        if (minimumAppearanceTimeReference.current < 150) minimumAppearanceTimeReference.current = 150;
        if (maximumAppearanceTimeReference.current < 300) maximumAppearanceTimeReference.current = 300;

        // Programar el siguiente globo de manera secuencial (solo 1 a la vez)
        const delayBeforeNext = Math.random() * 300 + 100;
        targetAppearanceTimeoutReference.current = setTimeout(spawnRandomTarget, delayBeforeNext);
      }, timeToStayUp);
    };

    spawnRandomTarget();

    phaseTimerReference.current = setInterval(() => {
      remainingTimeInSeconds--;
      
      if (scheduledBombSecondsList.includes(remainingTimeInSeconds)) isBombPendingReference.current = true;
      
      if (remainingTimeInSeconds <= 0) {
        isPhaseRunningRef.current = false; // Detener apariciones instantáneamente
        clearInterval(phaseTimerReference.current);
        clearTimeout(targetAppearanceTimeoutReference.current);
        setCurrentPhaseIndex(prevIndex => prevIndex + 1); 
      }
    }, 1000);
  }, [currentPhaseIndex]); // Añadimos currentPhaseIndex a las dependencias para el mensaje de bienvenida

  // --- EJECUTOR: FASE BONUS ---
  const startBonusPhase = useCallback((phaseDuration) => {
    isPhaseRunningRef.current = false;
    setHolesGridState(Array(6).fill({ isRaised: false, targetType: 'normal', isHit: false }));
    showTemporaryCenterAlert("¡BONUS!", "#fbbf24");
    
    setTimeout(() => {
      setCurrentBackgroundImageSrc(GAME_ASSETS.BACKGROUND_BONUS);
      setCurrentBonusImageSrc(GAME_ASSETS.ELEMENT_BONUS_1);
      
      const aestheticColorsList = ['#f97316', '#ef4444', '#3b82f6', '#8b5cf6', '#10b981'];
      const generatedTapLabels = Array.from({ length: 7 }).map((_, mappedIndex) => ({
        id: mappedIndex,
        leftPosition: `${Math.random() * 80 + 5}%`,
        topPosition: `${Math.random() * 25 + 65}%`,
        backgroundColor: aestheticColorsList[Math.floor(Math.random() * aestheticColorsList.length)],
        animationDelay: `${Math.random() * 2}s`
      }));
      setBonusTapLabelsArray(generatedTapLabels);

      setTimeout(() => setCurrentBonusImageSrc(GAME_ASSETS.ELEMENT_BONUS_2), (phaseDuration * 1000) - 1000);

      setTimeout(() => {
        setCurrentPhaseIndex(prevIndex => prevIndex + 1);
      }, phaseDuration * 1000);
    }, 1000);
  }, []);

  // --- FIN DE TODAS LAS FASES ---
  const finalizeGameEngine = () => {
    if (hasGameFinishedReference.current) return;
    hasGameFinishedReference.current = true;
    setCurrentBackgroundImageSrc(GAME_ASSETS.BACKGROUND_NORMAL);
    if (onGameFinishEvent) onGameFinishEvent(mutableScoreReference.current);
  };

  // --- INTERACCIONES DEL JUGADOR ---
  const triggerScreenFlash = (flashType) => {
    if (flashType === 'hit') { setIsHitFlashActive(false); setTimeout(() => setIsHitFlashActive(true), 10); }
    if (flashType === 'miss') { setIsMissFlashActive(false); setTimeout(() => setIsMissFlashActive(true), 10); }
  };

  const createFloatingTextAnimation = (xCoordinate, yCoordinate, displayText, textColor) => {
    const uniqueElementId = Date.now();
    setFloatingTextsArray(previousTexts => [...previousTexts, { id: uniqueElementId, x: xCoordinate, y: yCoordinate, text: displayText, color: textColor }]);
    setTimeout(() => setFloatingTextsArray(previousTexts => previousTexts.filter(textElement => textElement.id !== uniqueElementId)), 400);
  };

  const showTemporaryCenterAlert = (displayText, textColor) => {
    setStatusAlertObject({ text: displayText, color: textColor });
    setTimeout(() => setStatusAlertObject(null), 1000);
  };

  const handleTargetWhackInteraction = (gridIndex, touchEvent) => {
    touchEvent.stopPropagation();
    
    if (activePhaseType !== 'normal') return;
    
    const targetData = holesGridState[gridIndex];
    if (isPlayerBlindedReference.current || !targetData.isRaised || targetData.isHit) return;

    const elementBoundingRect = touchEvent.target.getBoundingClientRect();
    const interactionXCoordinate = touchEvent.clientX || (touchEvent.touches && touchEvent.touches[0].clientX) || elementBoundingRect.left;
    const interactionYCoordinate = touchEvent.clientY || (touchEvent.touches && touchEvent.touches[0].clientY) || elementBoundingRect.top;

    setHolesGridState(previousGridState => {
      const updatedGridState = [...previousGridState];
      updatedGridState[gridIndex].isHit = true;
      return updatedGridState;
    });

    if (targetData.targetType === 'bomb') {
      const calculatedPenalizedScore = Math.max(0, mutableScoreReference.current - 5);
      mutableScoreReference.current = calculatedPenalizedScore;
      setCurrentScore(calculatedPenalizedScore);
      createFloatingTextAnimation(interactionXCoordinate, interactionYCoordinate, "-5", "#ef4444");
      executeBlindBombEffect();
    } else {
      mutableScoreReference.current += 3;
      setCurrentScore(mutableScoreReference.current);
      createFloatingTextAnimation(interactionXCoordinate, interactionYCoordinate, "¡+3!", "#facc15");
      triggerScreenFlash('hit');
    }

    setTimeout(() => {
      activeHolesRef.current[gridIndex] = false; // Liberar hoyo tras golpe
      setHolesGridState(previousGridState => {
        const safeGridState = [...previousGridState];
        safeGridState[gridIndex].isRaised = false;
        return safeGridState;
      });
    }, 400);
  };

  const handleBonusTargetTapInteraction = (touchEvent) => {
    touchEvent.stopPropagation();
    if (activePhaseType !== 'bonus') return;

    mutableScoreReference.current += 1;
    setCurrentScore(mutableScoreReference.current);
    triggerScreenFlash('hit');
    
    const interactionXCoordinate = touchEvent.clientX || (touchEvent.touches && touchEvent.touches[0].clientX);
    const interactionYCoordinate = touchEvent.clientY || (touchEvent.touches && touchEvent.touches[0].clientY);
    if (interactionXCoordinate && interactionYCoordinate) createFloatingTextAnimation(interactionXCoordinate, interactionYCoordinate, "¡+1!", "#facc15");
  };

  const handleBackgroundMissInteraction = () => {
    if (activePhaseType === 'normal' && !isPlayerBlindedReference.current) {
      const calculatedPenalizedScore = Math.max(0, mutableScoreReference.current - 1);
      mutableScoreReference.current = calculatedPenalizedScore;
      setCurrentScore(calculatedPenalizedScore);
      triggerScreenFlash('miss');
    }
  };

  const executeBlindBombEffect = () => {
    isPlayerBlindedReference.current = true;
    setIsScreenShaking(true);
    setIsBombFlashActive(true);
    showTemporaryCenterAlert("¡CEGADO!", "#ffffff");

    setTimeout(() => setIsBombFlashActive(false), 1000); 
    setTimeout(() => {
      isPlayerBlindedReference.current = false;
      setIsScreenShaking(false);
    }, 3000);
  };

  // --- RENDERIZADO ---
  if (currentGameState === 'preloading') {
    return <div className="lucho-fest__overlay lucho-fest__overlay--dark"><div className="lucho-fest__countdown-text">...</div></div>;
  }

  return (
    <div className={`lucho-fest ${isScreenShaking ? 'lucho-fest--shaking' : ''}`} style={{ backgroundImage: `url(${currentBackgroundImageSrc})` }}>
      
      <div className={`lucho-fest__flash lucho-fest__flash--hit ${isHitFlashActive ? 'lucho-fest__flash--active' : ''}`} />
      <div className={`lucho-fest__flash lucho-fest__flash--miss ${isMissFlashActive ? 'lucho-fest__flash--active' : ''}`} />
      <div className="lucho-fest__flash lucho-fest__flash--bomb" style={{ opacity: isBombFlashActive ? 1 : 0, transition: isBombFlashActive ? 'none' : 'opacity 2s linear' }} />

      {statusAlertObject && <div className="lucho-fest__status-alert" style={{ color: statusAlertObject.color }}>{statusAlertObject.text}</div>}
      {floatingTextsArray.map(floatingTextElement => (
        <div key={floatingTextElement.id} className="lucho-fest__floating-text font-bold" style={{ left: floatingTextElement.x, top: floatingTextElement.y, color: floatingTextElement.color }}>
          {floatingTextElement.text}
        </div>
      ))}

      {currentGameState === 'countdown' && (
        <div className="lucho-fest__overlay lucho-fest__overlay--dark">
          <div className="lucho-fest__countdown-text">{countdownSeconds}</div>
        </div>
      )}

      {/* RENDERIZADO CONDICIONAL DE LA FASE ACTUAL */}
      {activePhaseType === 'bonus' && (
        <div className="lucho-fest__overlay lucho-fest__overlay--bonus">
          <img src={GAME_ASSETS.BRAND} alt="Bonus Banner" className="lucho-fest__bonus-banner" />
          <div className="lucho-fest__tap-container">
            {bonusTapLabelsArray.map(labelConfig => (
              <div key={labelConfig.id} className="lucho-fest__tap-label font-bold" style={{ left: labelConfig.leftPosition, top: labelConfig.topPosition, backgroundColor: labelConfig.backgroundColor, animationDelay: labelConfig.animationDelay }}>
                TAP
              </div>
            ))}
          </div>
          <div className="lucho-fest__bonus-target" onPointerDown={handleBonusTargetTapInteraction}>
            <img src={currentBonusImageSrc} alt="Bonus Target" />
          </div>
        </div>
      )}

      {activePhaseType === 'normal' && (
        <div className="lucho-fest__main-wrapper" onPointerDown={handleBackgroundMissInteraction}>
          <div className="lucho-fest__game-area">
            {holesGridState.map((holeData, mappedIndex) => (
              <div key={mappedIndex} className="lucho-fest__hole" style={{ backgroundImage: `url(${GAME_ASSETS.BOX})` }}>
                <div className={`lucho-fest__target ${holeData.isRaised ? 'lucho-fest__target--up' : ''}`} onPointerDown={(event) => handleTargetWhackInteraction(mappedIndex, event)}>
                  <img src={holeData.targetType === 'bomb' ? GAME_ASSETS.ELEMENT_WRONG : (holeData.isHit ? GAME_ASSETS.ELEMENT_HIT : GAME_ASSETS.ELEMENT_NORMAL)} alt="Target" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export { LuchoFestGame };